import React, { useRef, useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import styled from 'styled-components';
import { Typography, Box, Chip, CircularProgress, Alert, IconButton, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Schedule, LocationOn, Star as StarIcon, ZoomOutMap as ZoomOutMapIcon, NavigateNext, NavigateBefore } from '@mui/icons-material';
import axios from 'axios';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8000';

// Custom marker icon with shadow and retina support
const customIcon = L.icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const MapViewContainer = styled(motion.div)`
  position: relative;
  height: 100vh;
  width: 100vw;
  background: #f8fafc;
  overflow: hidden;
`;

const ControlsContainer = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledIconButton = styled(IconButton)`
  background-color: white !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
  border: 1px solid rgba(0, 0, 0, 0.04) !important;
  backdrop-filter: blur(8px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;

  &:hover {
    background-color: #f8fafc !important;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12) !important;
  }

  &:active {
    transform: translateY(0);
  }
`;

const NavigationControls = styled.div`
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  display: flex;
  gap: 12px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const StyledPopup = styled.div`
  min-width: 300px;
  max-width: 320px;
  padding: 0;
  border-radius: 16px;
  background-color: white;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06),
    0 20px 25px -5px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transform-origin: 50% 50%;
  animation: popupEnter 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  @keyframes popupEnter {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(10px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  .popup-image {
    position: relative;
    width: 100%;
    height: 200px;
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0.3) 0%,
        rgba(0, 0, 0, 0) 30%
      );
      z-index: 1;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.6s cubic-bezier(0.215, 0.61, 0.355, 1);
      
      &:hover {
        transform: scale(1.05);
      }
    }
  }

  .popup-content {
    padding: 20px;
  }

  .popup-header {
    margin-bottom: 16px;

    h6 {
      font-weight: 600;
      font-size: 1.15rem;
      margin-bottom: 4px;
      color: #1a1a1a;
      line-height: 1.3;
      letter-spacing: -0.01em;
    }

    .location-type {
      display: inline-flex;
      align-items: center;
      background: ${alpha('#64748b', 0.1)};
      color: #64748b;
      font-size: 0.85rem;
      padding: 4px 8px;
      border-radius: 6px;
      margin-top: 8px;
    }

    .opening-status {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: 8px;
      font-size: 0.9rem;
      color: #64748b;

      &.open {
        color: #10b981;
      }
      
      &.closed {
        color: #ef4444;
      }
    }
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-bottom: 16px;

    .info-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px;
      background: ${alpha('#f1f5f9', 0.5)};
      border-radius: 8px;
      transition: all 0.2s ease;

      &:hover {
        background: ${alpha('#f1f5f9', 0.8)};
      }

      .MuiSvgIcon-root {
        font-size: 1.1rem;
        color: #64748b;
      }

      .info-text {
        font-size: 0.9rem;
        color: #334155;
        font-weight: 500;
      }
    }
  }

  .popup-footer {
    display: flex;
    gap: 8px;
    padding: 16px 20px;
    background: ${alpha('#f8fafc', 0.5)};
    border-top: 1px solid ${alpha('#64748b', 0.1)};

    .MuiChip-root {
      background: ${alpha('#0ea5e9', 0.1)};
      color: #0ea5e9;
      font-weight: 600;
      height: 28px;
      
      &:hover {
        background: ${alpha('#0ea5e9', 0.15)};
      }
    }

    .action-buttons {
      display: flex;
      gap: 8px;
      margin-left: auto;
    }

    a {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      color: #0ea5e9;
      text-decoration: none;
      font-size: 0.9rem;
      font-weight: 600;
      padding: 6px 12px;
      border-radius: 8px;
      background: ${alpha('#0ea5e9', 0.1)};
      transition: all 0.2s ease;

      &:hover {
        background: ${alpha('#0ea5e9', 0.15)};
        transform: translateY(-1px);
      }

      &:active {
        transform: translateY(0);
      }

      .MuiSvgIcon-root {
        font-size: 1rem;
      }
    }
  }
`;

const LoadingContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: #f8fafc;

  .MuiCircularProgress-root {
    color: #0ea5e9;
  }
`;

const StyledAlert = styled(Alert)`
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  border-radius: 12px;
  padding: 12px 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(8px);
`;

const MapView = ({ activities }) => {
  const mapRef = useRef(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [map, setMap] = useState(null);
  const [showWorkingHours, setShowWorkingHours] = useState({});

  // Memoize filtered locations
  const validLocations = useMemo(() => {
    if (!activities?.length) return [];
    
    // Filter out activities without valid coordinates
    return activities.filter(loc => 
      loc.latitude && 
      loc.longitude && 
      !isNaN(parseFloat(loc.latitude)) && 
      !isNaN(parseFloat(loc.longitude))
    );
  }, [activities]);

  // Find the first valid location for initial center
  const initialCenter = useMemo(() => {
    const validLocation = validLocations.find(loc => 
      loc.latitude && 
      loc.longitude && 
      !isNaN(parseFloat(loc.latitude)) && 
      !isNaN(parseFloat(loc.longitude))
    );
    return validLocation 
      ? [parseFloat(validLocation.latitude), parseFloat(validLocation.longitude)]
      : [25.7617, -80.1918]; // Default to Miami coordinates if no valid location found
  }, [validLocations]);

  useEffect(() => {
    if (activities?.length > 0) {
      // Remove duplicates based on activity and location
      const uniqueLocations = activities.reduce((acc, curr) => {
        const key = `${curr.activity}-${curr.location}`;
        if (!acc.some(loc => `${loc.activity}-${loc.location}` === key)) {
          acc.push(curr);
        }
        return acc;
      }, []);
      setLocations(uniqueLocations);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [activities]);

  // Memoize markers
  const markers = useMemo(() => (
    <MarkerClusterGroup
      chunkedLoading
      maxClusterRadius={50}
      spiderfyOnMaxZoom={true}
      removeOutsideVisibleBounds={true}
    >
      {locations.map((location, index) => {
        const lat = parseFloat(location.latitude);
        const lng = parseFloat(location.longitude);
        
        // Skip invalid coordinates
        if (isNaN(lat) || isNaN(lng)) return null;
        
        return (
          <Marker
            key={`${location.activity}-${location.location}-${index}`}
            position={[lat, lng]}
            icon={customIcon}
            eventHandlers={{
              click: () => handleLocationSelect(location, index),
            }}
          >
            <Popup>
              <StyledPopup>
                {location.photo_url && (
                  <div className="popup-image">
                    <img 
                      src={location.photo_url}
                      alt={location.activity}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/400x300?text=No+Image+Available';
                      }}
                    />
                  </div>
                )}
                <div className="popup-content">
                  <div className="popup-header">
                    <Typography variant="h6">{location.activity}</Typography>
                    {location.business_type && (
                      <div className="location-type">
                        {location.business_type}
                      </div>
                    )}
                    {location.opening_status && (
                      <div className={`opening-status ${location.opening_status.toLowerCase().includes('open') ? 'open' : 'closed'}`}>
                        <div className="status-dot" style={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          background: location.opening_status.toLowerCase().includes('open') ? '#10b981' : '#ef4444' 
                        }} />
                        {location.opening_status}
                        {location.working_hours && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setShowWorkingHours(prev => ({
                                ...prev,
                                [location.place_id]: !prev[location.place_id]
                              }));
                            }}
                            sx={{ ml: 1 }}
                          >
                            <Schedule fontSize="small" />
                          </IconButton>
                        )}
                      </div>
                    )}
                    {showWorkingHours[location.place_id] && location.working_hours && (
                      <Box sx={{ 
                        mt: 2,
                        p: 2,
                        borderRadius: 1,
                        bgcolor: 'rgba(0, 0, 0, 0.02)',
                        fontSize: '0.85rem'
                      }}>
                        {Object.entries(location.working_hours).map(([day, hours]) => (
                          <Box key={day} sx={{ 
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 0.5,
                            '&:last-child': { mb: 0 }
                          }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 100 }}>
                              {day}:
                            </Typography>
                            <Typography variant="caption">
                              {hours}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </div>

                  <div className="info-grid">
                    {location.time && (
                      <div className="info-item">
                        <Schedule />
                        <span className="info-text">{location.time}</span>
                      </div>
                    )}
                    {location.location && (
                      <div className="info-item">
                        <LocationOn />
                        <span className="info-text">{location.location}</span>
                      </div>
                    )}
                    {location.rating && (
                      <div className="info-item">
                        <StarIcon color="warning" />
                        <span className="info-text">
                          {typeof location.rating === 'string' 
                            ? parseFloat(location.rating).toFixed(1) 
                            : location.rating.toFixed(1)
                          } ({location.review_count || 0})
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="popup-footer">
                    <Chip label={`Day ${location.day}`} />
                    <div className="action-buttons">
                      {location.place_link && (
                        <a href={location.place_link} target="_blank" rel="noopener noreferrer">
                          View on Maps
                        </a>
                      )}
                      {location.website && (
                        <a href={location.website} target="_blank" rel="noopener noreferrer">
                          Visit Website
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </StyledPopup>
            </Popup>
          </Marker>
        );
      })}
    </MarkerClusterGroup>
  ), [locations]);

  useEffect(() => {
    if (locations.length > 0 && map) {
      const validCoords = locations
        .filter(item => !isNaN(parseFloat(item.latitude)) && !isNaN(parseFloat(item.longitude)))
        .map(item => [parseFloat(item.latitude), parseFloat(item.longitude)]);
      
      if (validCoords.length > 0) {
        const bounds = L.latLngBounds(validCoords);
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [locations, map]);

  const handleMapCreated = (mapInstance) => {
    setMap(mapInstance);
    mapRef.current = mapInstance;
  };

  const handleLocationSelect = (location, index) => {
    setSelectedLocation(location);
    setCurrentIndex(index);
    if (mapRef.current) {
      mapRef.current.setView([parseFloat(location.latitude), parseFloat(location.longitude)], 16, {
        animate: true,
        duration: 0.8
      });
    }
  };

  const handleFitBounds = () => {
    if (mapRef.current && locations.length > 0) {
      const bounds = L.latLngBounds(locations.map(item => [parseFloat(item.latitude), parseFloat(item.longitude)]));
      mapRef.current.fitBounds(bounds, { 
        padding: [50, 50],
        animate: true,
        duration: 0.8
      });
    }
  };

  const navigateLocation = (direction) => {
    const newIndex = direction === 'next'
      ? (currentIndex + 1) % locations.length
      : (currentIndex - 1 + locations.length) % locations.length;
    
    const location = locations[newIndex];
    handleLocationSelect(location, newIndex);
  };

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress size={40} thickness={4} />
      </LoadingContainer>
    );
  }

  if (error) {
    return <StyledAlert severity="error">{error}</StyledAlert>;
  }

  if (!locations || locations.length === 0) {
    return <StyledAlert severity="info">No location data available</StyledAlert>;
  }

  return (
    <MapViewContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <MapContainer
        style={{ height: '100%', width: '100%' }}
        center={initialCenter}
        zoom={13}
        whenCreated={handleMapCreated}
        zoomControl={false}
        maxZoom={18}
        minZoom={3}
        preferCanvas={true}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          maxZoom={18}
          minZoom={3}
          updateWhenIdle={true}
          updateWhenZooming={false}
          keepBuffer={2}
        />
        {markers}
      </MapContainer>

      <ControlsContainer>
        <Tooltip title="Fit all locations" placement="left">
          <StyledIconButton onClick={handleFitBounds} size="large">
            <ZoomOutMapIcon />
          </StyledIconButton>
        </Tooltip>
      </ControlsContainer>

      {locations.length > 1 && (
        <NavigationControls>
          <Tooltip title="Previous location" placement="top">
            <StyledIconButton 
              onClick={() => navigateLocation('prev')}
              disabled={locations.length <= 1}
            >
              <NavigateBefore />
            </StyledIconButton>
          </Tooltip>
          <Tooltip title="Next location" placement="top">
            <StyledIconButton 
              onClick={() => navigateLocation('next')}
              disabled={locations.length <= 1}
            >
              <NavigateNext />
            </StyledIconButton>
          </Tooltip>
        </NavigationControls>
      )}
    </MapViewContainer>
  );
};

export default MapView;